import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChatMessage } from '../shared/types';

interface ChatProps {
  messages: ChatMessage[];
  onSend: (text: string) => void;
  viewerPlayerId: string;
  open: boolean;
  onToggle: () => void;
}

export function TableChat({ messages, onSend, viewerPlayerId, open, onToggle }: ChatProps) {
  const [text, setText] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  function submit() {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setText('');
  }

  return (
    <>
      <button
        onClick={onToggle}
        className="fixed bottom-24 right-3 z-30 w-11 h-11 rounded-full bg-ink-800/90 border border-gold-500/30 flex items-center justify-center shadow-card"
      >
        💬
        {messages.length > 0 && !open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 text-[9px] flex items-center justify-center text-ink-950 font-bold">
            {Math.min(9, messages.length)}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            className="fixed bottom-0 left-0 right-0 z-30 h-72 bg-ink-900/95 backdrop-blur-md border-t border-gold-500/20 rounded-t-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
              <span className="text-sm font-semibold text-white/80">Чат стола</span>
              <button onClick={onToggle} className="text-white/40 text-sm">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-2 space-y-1.5">
              {messages.map((m) => (
                <div key={m.id} className={`text-sm ${m.playerId === viewerPlayerId ? 'text-emerald-300' : 'text-white/70'}`}>
                  <span className="font-semibold">{m.username}: </span>
                  <span>{m.text}</span>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="flex gap-2 p-3 border-t border-white/5">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                maxLength={300}
                placeholder="Сообщение…"
                className="flex-1 bg-ink-800 rounded-lg px-3 py-2 text-sm text-white/90 outline-none focus:ring-1 focus:ring-gold-500/50"
              />
              <button onClick={submit} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold">
                Отправить
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
